import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Clock, TrendingUp, Eye, Zap, Users, Trash2, RefreshCw, Share2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePricing } from '../hooks/usePricing';
import api from '../lib/api';
import ShareJobDialog from './ShareJobDialog';
import { toast } from 'sonner';

const JobPostingsTable = ({ jobs, isArchived, refetchCredits, applicationsCount, unviewedCounts, onMarkFilled, onDeleteDraft, pesoPending }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pricing } = usePricing();
  const [showBoostDialog, setShowBoostDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [boosting, setBoosting] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const [deletingDraftId, setDeletingDraftId] = useState(null);
  const [renewingJobId, setRenewingJobId] = useState(null);
  const [shareJob, setShareJob] = useState(null); // open job whose share panel is showing

  // Helper to get job/gig URL (use slug if available, otherwise ID)
  const getJobUrl = (job) => {
    const basePath = job.post_type === 'gig' ? '/gigs' : '/jobs';
    return job.slug ? `${basePath}/${job.slug}` : `${basePath}/${job.id}`;
  };

  // Sort jobs by created_at DESC (newest first)
  const sortedJobs = [...jobs].sort((a, b) => 
    new Date(b.created_at) - new Date(a.created_at)
  );

  const visibleJobs = sortedJobs.slice(0, visibleCount);
  const hasMore = sortedJobs.length > visibleCount;

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 10);
  };

  const getApplicationsForJob = (jobId) => {
    return applicationsCount?.[jobId] || 0;
  };

  const getUnviewedCount = (jobId) => {
    return unviewedCounts?.[jobId] || 0;
  };

  const handleBoostClick = (e, job) => {
    e.stopPropagation();
    setSelectedJob(job);
    setShowBoostDialog(true);
  };

  const confirmBoost = async () => {
    if (!selectedJob) return;
    
    setBoosting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      await api.post(`/api/jobs/${selectedJob.id}/boost`, {}, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      toast.success('Job boosted to top of listings!');
      setShowBoostDialog(false);
      setSelectedJob(null);
      refetchCredits();
      window.location.reload();
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error(`Insufficient credits. Need ${pricing.boost_post} credits to boost.`);
        navigate('/credits');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to boost job');
      }
    } finally {
      setBoosting(false);
    }
  };

  const handleMarkFilled = (e, job) => {
    e.stopPropagation();
    if (onMarkFilled) {
      onMarkFilled(job);
    }
  };

  const handleDeleteDraft = async (e, job) => {
    e.stopPropagation();
    if (!window.confirm(t('tables.confirmDeleteDraft', 'Delete this draft? This cannot be undone.'))) return;
    setDeletingDraftId(job.id);
    try {
      const { data: session } = await supabase.auth.getSession();
      const endpoint = job.post_type === 'gig' ? `/api/gigs/${job.id}` : `/api/jobs/${job.id}`;
      await api.delete(endpoint, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      // Clean up localStorage wizard state
      try { localStorage.removeItem(`draft-mode-${job.id}`); } catch {}
      try { localStorage.removeItem(`draft-wizard-${job.id}`); } catch {}
      toast.success(t('tables.draftDeleted', 'Draft deleted'));
      if (onDeleteDraft) onDeleteDraft(job.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete draft');
    }
    setDeletingDraftId(null);
  };

  const handleRenewJob = async (e, job) => {
    e.stopPropagation();
    setRenewingJobId(job.id);
    try {
      const { data: session } = await supabase.auth.getSession();
      const endpoint = job.post_type === 'gig' ? `/api/gigs/${job.id}/renew` : `/api/jobs/${job.id}/renew`;
      await api.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      toast.success(t('tables.renewedSuccess', 'Renewed for 60 days!'));
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('tables.renewFailed', 'Failed to renew'));
    }
    setRenewingJobId(null);
  };

  return (
    <>
    <div className="space-y-4">
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b bg-muted/30">
              <tr>
                <th className="py-3 px-4 text-left font-semibold text-sm">{t('tables.jobTitle')}</th>
                <th className="py-3 px-4 text-left font-semibold text-sm hidden lg:table-cell">{t('tables.location')}</th>
                <th className="py-3 px-4 text-left font-semibold text-sm hidden md:table-cell">{t('tables.status')}</th>
                <th className="py-3 px-4 text-left font-semibold text-sm hidden sm:table-cell">{t('tables.applications')}</th>
                <th className="py-3 px-4 text-left font-semibold text-sm">{t('tables.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {visibleJobs.map((job) => {
                const isFeatured = job.is_featured && job.featured_until && new Date(job.featured_until) > new Date();
                const isBoosted = job.boosted_at && (new Date() - new Date(job.boosted_at)) < 24 * 60 * 60 * 1000;
                const daysRemaining = isFeatured ? Math.ceil((new Date(job.featured_until) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                const expiresAt = job.expires_at ? new Date(job.expires_at) : null;
                const daysUntilExpiration = expiresAt ? Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24)) : null;
                const isExpired = job.status === 'expired' || (expiresAt && expiresAt < new Date());
                const isExpiringSoon = daysUntilExpiration && daysUntilExpiration <= 7 && daysUntilExpiration > 0;
                const appCount = getApplicationsForJob(job.id);

                return (
                  <tr
                    key={job.id}
                    className="hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => job.status === 'draft' ? navigate(`/post-job?draft=${job.id}`) : navigate(getJobUrl(job))}
                    data-testid={`job-row-${job.id}`}
                  >
                    <td className="py-3 px-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium">{job.title}</span>
                          {job.post_type === 'gig' && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              {t('tables.gig')}
                            </Badge>
                          )}
                          {isFeatured && (
                            <Badge className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white text-xs">
                              ⭐ {t('tables.daysRemaining', { days: daysRemaining })}
                            </Badge>
                          )}
                          {isBoosted && (
                            <Badge className="bg-gradient-to-r from-purple-500 to-purple-600 text-white text-xs">
                              🚀
                            </Badge>
                          )}
                          {getUnviewedCount(job.id) > 0 && (
                            <Badge variant="default" className="bg-primary text-white text-xs sm:hidden">
                              {t('tables.newApplications', { count: getUnviewedCount(job.id) })}
                            </Badge>
                          )}

                          {/* Quick Filter Badges */}
                          {job.positions_available > 1 && (
                            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-medium dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800">
                              <Users size={10} /> Hiring {job.positions_available}
                            </span>
                          )}
                          {job.urgent_hiring && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium">
                              🔴 Urgent
                            </span>
                          )}
                          {job.no_experience_needed && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-medium">
                              ⭐ No Exp
                            </span>
                          )}
                          {job.with_housing && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                              🏠 Housing
                            </span>
                          )}
                        </div>
                        {/* Mobile: Show location and status inline */}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground lg:hidden">
                          <span>{(() => { const locs = (job.locations || [job.location]).filter(Boolean); return locs.length <= 3 ? locs.join(', ') : `${locs.slice(0, 3).join(', ')} +${locs.length - 3} more`; })()}</span>
                          <span>•</span>
                          <Badge
                            variant={
                              job.status === 'draft' ? 'outline' :
                              job.status === 'filled' || job.status === 'closed' ? 'secondary' :
                              job.status === 'expired' || isExpired ? 'destructive' :
                              'default'
                            }
                            className={`text-xs ${job.status === 'draft' ? 'border-amber-300 text-amber-700 bg-amber-50' : ''}`}
                          >
                            {job.status === 'expired' || isExpired ? t('tables.expired') : (pesoPending && job.status === 'draft') ? t('tables.pendingPesoApproval', 'Pending PESO approval') : t(`tables.${job.status}`)}
                          </Badge>
                        </div>
                        {/* Expiration warning */}
                        {job.status === 'open' && isExpiringSoon && (
                          <div className="flex items-center gap-1 text-xs text-orange-500 mt-1">
                            <Clock size={12} />
                            <span>{t('tables.expiresIn', { days: daysUntilExpiration })}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground hidden lg:table-cell">
                      {(() => { const locs = (job.locations || [job.location]).filter(Boolean); return locs.length <= 3 ? locs.join(', ') : `${locs.slice(0, 3).join(', ')} +${locs.length - 3} more`; })()}
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell">
                      <Badge
                        variant={
                          job.status === 'draft' ? 'outline' :
                          job.status === 'filled' || job.status === 'closed' ? 'secondary' :
                          job.status === 'expired' || isExpired ? 'destructive' :
                          'default'
                        }
                        className={job.status === 'draft' ? 'border-amber-300 text-amber-700 bg-amber-50' : ''}
                        data-testid={`job-status-${job.id}`}
                      >
                        {job.status === 'expired' || isExpired ? t('tables.expired') : (pesoPending && job.status === 'draft') ? t('tables.pendingPesoApproval', 'Pending PESO approval') : t(`tables.${job.status}`)}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 hidden sm:table-cell">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Eye size={14} className="text-muted-foreground" />
                          <span className="text-sm font-medium">{appCount}</span>
                        </div>
                        {getUnviewedCount(job.id) > 0 && (
                          <Badge variant="default" className="bg-primary text-white text-xs px-1.5">
                            {t('tables.newApplications', { count: getUnviewedCount(job.id) })}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                        {/* Renew / "Still hiring?": shown for expired jobs wherever they live
                            (they now stay in the active list) and for filled jobs in the archive
                            (Repost). Renewal resets the posted date and re-lists for 60 days. */}
                        {(isExpired || job.status === 'expired' || job.status === 'filled') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => handleRenewJob(e, job)}
                            disabled={renewingJobId === job.id}
                            data-testid={`renew-job-${job.id}`}
                            className="text-xs border-green-500 text-green-600 hover:bg-green-50 hover:text-green-700"
                          >
                            <RefreshCw size={14} className={`mr-1 ${renewingJobId === job.id ? 'animate-spin' : ''}`} />
                            <span className="hidden sm:inline">{renewingJobId === job.id ? t('tables.renewing', 'Renewing...') : job.status === 'filled' ? t('tables.repost', 'Repost') : t('tables.stillHiringRenew', 'Still hiring? Renew')}</span>
                            <span className="sm:hidden">{renewingJobId === job.id ? '...' : '↻'}</span>
                          </Button>
                        )}
                        {!isArchived && job.status === 'draft' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => handleDeleteDraft(e, job)}
                            disabled={deletingDraftId === job.id}
                            className="text-xs border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 size={14} className="mr-1" />
                            <span className="hidden sm:inline">{deletingDraftId === job.id ? t('tables.deleting', 'Deleting...') : t('tables.discard', 'Discard')}</span>
                            <span className="sm:hidden">{deletingDraftId === job.id ? '...' : '✕'}</span>
                          </Button>
                        )}
                        {!isArchived && job.status === 'open' && !isExpired && (
                          <>
                            {job.post_type !== 'gig' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); setShareJob(job); }}
                                data-testid={`share-job-${job.id}`}
                                className="text-xs border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                                aria-label={t('shareJob.shareButton', 'Share')}
                              >
                                <Share2 size={14} className="sm:mr-1" />
                                <span className="hidden sm:inline">{t('shareJob.shareButton', 'Share')}</span>
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => handleBoostClick(e, job)}
                              data-testid={`boost-job-${job.id}`}
                              className="text-xs"
                            >
                              <TrendingUp size={14} className="mr-1" />
                              <span className="hidden sm:inline">{t('tables.boost')}</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => handleMarkFilled(e, job)}
                              data-testid={`mark-filled-${job.id}`}
                              className="text-xs border-green-500 text-green-600 hover:bg-green-50 hover:text-green-700"
                            >
                              <Clock size={14} className="mr-1" />
                              <span className="hidden sm:inline">{t('tables.markFilled')}</span>
                              <span className="sm:hidden">✓</span>
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (job.status === 'draft') {
                              navigate(`/post-job?draft=${job.id}`);
                            } else {
                              navigate(getJobUrl(job));
                            }
                          }}
                          data-testid={`view-job-${job.id}`}
                          className="text-xs"
                        >
                          <Eye size={14} className="mr-1" />
                          <span className="hidden sm:inline">{job.status === 'draft' ? t('tables.edit', 'Edit') : t('tables.view')}</span>
                          <span className="sm:hidden">{job.status === 'draft' ? '✎' : appCount}</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={handleLoadMore}
            className="rounded-xl"
            data-testid="load-more-job-postings"
          >
            {t('tables.loadMore')} ({t('tables.showingXofY', { visible: visibleCount, total: sortedJobs.length })})
          </Button>
        </div>
      )}
    </div>

    {/* Boost Confirmation Dialog */}
    <Dialog open={showBoostDialog} onOpenChange={setShowBoostDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="text-yellow-500" />
            {selectedJob?.post_type === 'gig' ? t('tables.boostGigTitle') : t('tables.boostJobTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('tables.boostDescription', { credits: pricing.boost_post, type: selectedJob?.post_type === 'gig' ? 'gig' : 'trabaho' })}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <h4 className="font-semibold mb-2">{t('tables.boostToTop')}</h4>
            <p className="text-sm text-muted-foreground">"{selectedJob?.title}"</p>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <span className="font-medium">{t('postJob.cost')}:</span>
            <span className="text-xl font-bold text-primary">{pricing.boost_post} {t('postJob.credits')}</span>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setShowBoostDialog(false);
              setSelectedJob(null);
            }}
            disabled={boosting}
          >
            {t('dashboard.cancel')}
          </Button>
          <Button
            onClick={confirmBoost}
            disabled={boosting}
            data-testid="confirm-boost-btn"
          >
            {boosting ? t('tables.boosting') : t('tables.boostFor', { credits: pricing.boost_post })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <ShareJobDialog job={shareJob} open={!!shareJob} onClose={() => setShareJob(null)} />
  </>
  );
};

export default JobPostingsTable;
